import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import http from '@/services/http'

interface AccessCodeState {
  accessToken: string | null
  isVerified: boolean
  isLoading: boolean
  error: string | undefined
}

interface AccessCodeActions {
  verifyCode: (code: string) => Promise<void>
  checkTokenValidity: () => Promise<boolean>
  checkProtectionStatus: () => Promise<boolean>
  clearAccessToken: () => void
  setError: (error: string | undefined) => void
}

type AccessCodeStore = AccessCodeState & AccessCodeActions

const API_BASE = '/access'

export const useAccessCodeStore = create<AccessCodeStore>()(
  persist(
    (set, get) => ({
      accessToken: null,
      isVerified: false,
      isLoading: false,
      error: undefined,

      verifyCode: async (code: string) => {
        set({ isLoading: true, error: undefined })
        try {
          const response = await http.post(`${API_BASE}/verify-code`, { code })
          const token = response.data.access_token
          
          set({ 
            accessToken: token, 
            isVerified: true, 
            isLoading: false,
            error: undefined
          })
          
          localStorage.setItem('access-token', token)
        } catch (error: any) {
          const errorMsg = error.response?.data?.detail || 'Verification failed'
          set({ 
            isLoading: false, 
            error: errorMsg,
            isVerified: false
          })
          throw error
        }
      },

      checkTokenValidity: async () => {
        const token = get().accessToken || localStorage.getItem('access-token')
        
        if (!token) {
          return false
        }

        try {
          const response = await http.get(`${API_BASE}/verify-token`, { 
            params: { token } 
          })
          return response.data.valid === true
        } catch (error) {
          return false
        }
      },

      checkProtectionStatus: async () => {
        // 开发环境下默认关闭保护，方便直接预览
        if (import.meta.env.DEV) {
          console.log('[AccessCode] 开发环境：自动跳过访问码保护');
          return false;
        }
        
        try {
          const response = await http.get(`${API_BASE}/status`)
          // 后端返回 {"enabled": bool}
          return response.data.enabled === true
        } catch (error) {
          // 如果接口报错，通常意味着后端未就绪，默认开启保护以保证安全
          console.warn('[AccessCode] 无法获取保护状态，默认开启保护:', error)
          return true
        }
      },

      clearAccessToken: () => {
        set({ 
          accessToken: null, 
          isVerified: false,
          error: undefined
        })
        localStorage.removeItem('access-token')
      },

      setError: (error: string | undefined) => {
        set({ error })
      },
    }),
    {
      name: 'access-code-storage',
      partialize: (state) => ({ 
        accessToken: state.accessToken, 
        isVerified: state.isVerified 
      }),
    }
  )
)
